import type { AlgoliaCompanyRecord } from '../../types/algolia-company-record';

export type BestCompaniesHubData = { companies: AlgoliaCompanyRecord[] };

/** Prefer the hub payload with more `companies` rows (for merging `__VITE_PAGE_CONTEXT__.data` + inline JSON script on the client). */
export function richerBestCompaniesHubPayload(
  a: BestCompaniesHubData | undefined | null,
  b: BestCompaniesHubData | undefined | null,
): BestCompaniesHubData | undefined {
  const len = (x: BestCompaniesHubData | undefined | null) =>
    x && Array.isArray(x.companies) ? x.companies.length : 0;
  const la = len(a);
  const lb = len(b);
  if (la <= 0 && lb <= 0) return undefined;
  return la >= lb ? (a as BestCompaniesHubData) : (b as BestCompaniesHubData);
}

/** @internal Used in unit tests; production should omit this (Vike sets `import.meta.env.SSR`). */
export type BestCompaniesResolveOptions = {
  testIsSsr?: boolean;
};

function isSsrBundle(options?: BestCompaniesResolveOptions): boolean {
  if (options?.testIsSsr !== undefined) return options.testIsSsr;
  return import.meta.env.SSR === true;
}

/**
 * Merges `useData`, `pageContext.data`, and `window.__VITE_PAGE_CONTEXT__.data` for the best-companies hub.
 *
 * - **Browser:** If Vike inlined page data includes a non-empty `companies` array, use it as-is so the first
 *   hydrated render matches the SSR HTML (avoids `useData()` briefly reporting `[]` while the DOM still shows rows).
 *   Empty arrays are ignored so stale context from a previous route does not block real data.
 * - **SSR:** Pick the candidate with the longest `companies` list among hook / pageContext (serialized is absent).
 */
export function resolveBestCompaniesHubData(
  dataFromHook: BestCompaniesHubData | undefined | null,
  dataFromPageContext: BestCompaniesHubData | undefined | null,
  dataFromSerializedWindow: BestCompaniesHubData | undefined | null,
  options?: BestCompaniesResolveOptions,
): BestCompaniesHubData {
  if (
    !isSsrBundle(options) &&
    dataFromSerializedWindow != null &&
    Array.isArray(dataFromSerializedWindow.companies) &&
    dataFromSerializedWindow.companies.length > 0
  ) {
    return { companies: dataFromSerializedWindow.companies };
  }

  const ordered: Array<BestCompaniesHubData | undefined | null> = [
    dataFromHook,
    dataFromPageContext,
    dataFromSerializedWindow,
  ];
  let best: AlgoliaCompanyRecord[] | undefined;
  let bestLen = -1;
  for (const c of ordered) {
    if (!c || !Array.isArray(c.companies)) continue;
    if (c.companies.length > bestLen) {
      bestLen = c.companies.length;
      best = c.companies;
    }
  }
  return { companies: best ?? [] };
}

type DimensionDataLike = {
  config: unknown;
  companies?: AlgoliaCompanyRecord[];
};

/** Merge serialized page context + inline script snapshot for dimension pages (pick more company rows, require `config`). */
export function richerBestCompaniesDimensionPayload<T extends DimensionDataLike>(
  a: T | undefined | null,
  b: T | undefined | null,
): T | undefined {
  const score = (c: T | undefined | null) => {
    if (!c || c.config == null) return -1;
    return Array.isArray(c.companies) ? c.companies.length : 0;
  };
  const sa = score(a);
  const sb = score(b);
  if (sa < 0 && sb < 0) return undefined;
  return sa >= sb ? (a as T) : (b as T);
}

/**
 * Same hydration rules as the hub: on the client, trust inlined `__VITE_PAGE_CONTEXT__.data` when it has
 * `config` and a non-empty `companies` array. On SSR, pick the candidate with the most companies (ties:
 * hook, then pageContext, then serialized).
 */
export function resolveBestCompaniesDimensionData<T extends DimensionDataLike>(
  orderedCandidates: [T | undefined | null, T | undefined | null, T | undefined | null],
  options?: BestCompaniesResolveOptions,
): T | undefined {
  const [hook, pc, win] = orderedCandidates;
  if (
    !isSsrBundle(options) &&
    win != null &&
    win.config != null &&
    Array.isArray(win.companies) &&
    win.companies.length > 0
  ) {
    return win;
  }

  let maxLen = -1;
  for (const c of orderedCandidates) {
    if (c?.config == null) continue;
    const len = Array.isArray(c.companies) ? c.companies.length : 0;
    if (len > maxLen) maxLen = len;
  }
  if (maxLen < 0) return undefined;
  for (const c of orderedCandidates) {
    if (c?.config == null) continue;
    const len = Array.isArray(c.companies) ? c.companies.length : 0;
    if (len === maxLen) return c;
  }
  return undefined;
}
